import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
} from '@mui/material';
import { getRewardPointsSummary, calculatePointsRedemption, PointsRedemptionResponse, getRewardPointsSettings, RewardPointsSettings } from '../../api/profile';

interface UnifiedPointsComponentProps {
  isAuthenticated: boolean;
  onPointsApplied: (redemption: PointsRedemptionResponse | null) => void;
  appliedPoints: PointsRedemptionResponse | null;
  onOrderRecalculation?: () => void;
}

const UnifiedPointsComponent: React.FC<UnifiedPointsComponentProps> = ({
  isAuthenticated,
  onPointsApplied,
  appliedPoints,
  onOrderRecalculation,
}) => {
  const { t } = useTranslation();
  const [availablePoints, setAvailablePoints] = useState<number>(0);
  const [pointsToUse, setPointsToUse] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<RewardPointsSettings | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPointsData();
    } else {
      setAvailablePoints(0);
      onPointsApplied(null);
    }
  }, [isAuthenticated]);

  const fetchPointsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pointsData, settingsData] = await Promise.all([
        getRewardPointsSummary(),
        getRewardPointsSettings()
      ]);
      setAvailablePoints(pointsData.availablePoints || 0);
      setSettings(settingsData);
    } catch (err) {
      console.error('Failed to fetch reward points data:', err);
      setError(t('checkout.points.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // Allow empty input
    if (value === '') {
      setPointsToUse(0);
      setError(null);
      return;
    }
    
    // Parse the number
    const numValue = parseInt(value, 10);
    
    // If it's not a valid number, don't update
    if (isNaN(numValue)) {
      return;
    }
    
    // Allow typing any number, but show error if invalid
    setPointsToUse(numValue);
    
    if (!settings) {
      setError(null);
      return;
    }
    
    const minRedemption = settings.minimumRedemption || 0;
    const maxRedemption = settings.maximumRedemption;
    
    if (numValue < minRedemption) {
      setError(t('checkout.points.pointsRequired').replace('{min}', minRedemption.toString()));
    } else if (numValue > availablePoints) {
      setError(t('checkout.points.cannotUseMore'));
    } else if (maxRedemption && numValue > maxRedemption) {
      setError(t('checkout.points.maximumAllowed').replace('{max}', maxRedemption.toString()));
    } else {
      setError(null);
    }
  };

  const handleApplyPoints = async () => {
    if (!settings) {
      setError(t('checkout.points.settingsNotLoaded'));
      return;
    }
    
    const minRedemption = settings.minimumRedemption || 0;
    const maxRedemption = settings.maximumRedemption;
    
    if (pointsToUse < minRedemption || pointsToUse > availablePoints) {
      return;
    }
    
    if (maxRedemption && pointsToUse > maxRedemption) {
      setError(t('checkout.points.maximumAllowed', { max: maxRedemption }));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const redemptionData = await calculatePointsRedemption(pointsToUse);
      
      if (!redemptionData.isValid) {
        setError(t('checkout.points.invalidRedemption'));
        return;
      }
      
      onPointsApplied(redemptionData);
      // Trigger order summary recalculation after successful validation
      onOrderRecalculation?.();
    } catch (err) {
      console.error('Failed to apply points:', err);
      setError(t('checkout.points.failedToApply'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePoints = () => {
    onPointsApplied(null);
    setPointsToUse(0);
    setError(null);
    onOrderRecalculation?.();
  };

  if (!isAuthenticated) {
    return null; // Only show if user is authenticated
  }

  // If points are applied, show RewardPointsSection
  if (appliedPoints) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#FFA726',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
              }}
            >
              <Typography sx={{ fontSize: '12px', color: 'white', fontWeight: 'bold' }}>
                P
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: '#212529',
                mr: 1,
              }}
            >
              {t('checkout.points.rewardPoints')}
            </Typography>
            <Box
              sx={{
                bgcolor: '#FFF3E0',
                border: '1px solid #FFB74D',
                borderRadius: '20px',
                px: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#FF8F00',
                }}
              >
                {availablePoints} {t('checkout.points.points')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Applied Points Section */}
        <Paper
          sx={{
            bgcolor: '#FFFDE7',
            border: '1px solid #FDD835',
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#212529',
                  mb: 0.5,
                }}
              >
                {appliedPoints.pointsToRedeem} points applied
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#FF8F00',
                  fontWeight: 500,
                }}
              >
                Discount: -HK${appliedPoints.discountAmount.toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRemovePoints}
              sx={{
                borderColor: '#ddd',
                color: '#666',
                fontSize: '12px',
                px: 2,
                py: 0.5,
                minWidth: 'auto',
                '&:hover': {
                  borderColor: '#999',
                  bgcolor: '#f5f5f5',
                },
              }}
            >
              Remove
            </Button>
          </Box>
        </Paper>

        {/* Reward Rules */}
        <Box sx={{ pl: 1 }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#666',
              mb: 0.5,
            }}
          >
            • {t('profile.pointsRules.redeemRule').replace('{points}', (settings?.redeemRatePoints || 100).toString()).replace('{amount}', (settings?.redeemRateAmount || 1).toString())}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#666',
            }}
          >
            • {t('profile.pointsRules.earnRule').replace('{points}', (settings?.earnRatePoints || 1).toString()).replace('{amount}', (settings?.earnRateAmount || 1).toString())}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // If no points applied, show PointExchange
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        mb: 3,
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: '#B48B6A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
            }}
          >
            <Typography sx={{ fontSize: '14px', color: 'white', fontWeight: 'bold' }}>
              P
            </Typography>
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#212529',
              mr: 1,
            }}
          >
            {t('checkout.points.rewardPoints')}
          </Typography>
          <Box
            sx={{
              bgcolor: '#FFF3E0',
              border: '1px solid #FFB74D',
              borderRadius: '20px',
              px: 2,
              py: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#FF8F00',
              }}
            >
              {availablePoints} {t('checkout.points.points')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} sx={{ color: '#FF8F00' }} />
        </Box>
      ) : (
        <>
          {/* Points Input Section */}
          <Box sx={{ mb: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#212529',
                mb: 1,
              }}
            >
              {t('profile.points.pointsToRedeem')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size='small'
                variant="outlined"
                placeholder={`${t('profile.points.minimum')} ${settings?.minimumRedemption || 0}`}
                value={pointsToUse === 0 ? '' : pointsToUse}
                onChange={handleInputChange}
                type="number"
                inputProps={{ min: settings?.minimumRedemption || 0, max: availablePoints }}
                error={Boolean(error)}
                helperText={error}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF8F00',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF8F00',
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleApplyPoints}
                disabled={pointsToUse < (settings?.minimumRedemption || 0) || pointsToUse > availablePoints || loading || (settings?.maximumRedemption ? pointsToUse > settings.maximumRedemption : false)}
                sx={{
                  bgcolor: '#2196F3',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1976D2' },
                  px: 2,
                  borderRadius: '4px',
                  height: '40px',
                }}
              >
                {t('checkout.points.apply')}
              </Button>
            </Box>
          </Box>

          {/* Reward Rules */}
          <Box sx={{ mt: 2, pl: 1 }}>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#666',
                mb: 0.5,
              }}
            >
              • {t('profile.pointsRules.redeemRule').replace('{points}', (settings?.redeemRatePoints || 100).toString()).replace('{amount}', (settings?.redeemRateAmount || 1).toString())}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#666',
              }}
            >
              • {t('profile.pointsRules.earnRule').replace('{points}', (settings?.earnRatePoints || 1).toString()).replace('{amount}', (settings?.earnRateAmount || 1).toString())}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default UnifiedPointsComponent;
